import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_BADGES = {
  trial: 'bg-yellow-100 text-yellow-700',
  essentiel: 'bg-blue-100 text-blue-700',
  pro: 'bg-purple-100 text-purple-700',
};

const AdminMerchantsPage = () => {
  const [merchants, setMerchants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 15;

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('search', search);
      if (planFilter) params.set('plan', planFilter);
      const { data } = await axios.get(`${API_URL}/api/admin/merchants?${params}`, { withCredentials: true });
      setMerchants(data.merchants);
      setTotal(data.total);
    } catch {} finally { setLoading(false); }
  }, [page, search, planFilter]);

  useEffect(() => { fetchMerchants(); }, [fetchMerchants]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-merchants">
      <h1 className="text-2xl font-bold text-slate-900">Marchands</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher nom, email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="admin-merchant-search"
          />
        </div>
        <select
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
          data-testid="admin-plan-filter"
        >
          <option value="">Tous les plans</option>
          <option value="trial">Trial</option>
          <option value="essentiel">Essentiel</option>
          <option value="pro">Pro</option>
        </select>
      </div>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : merchants.length === 0 ? (
            <p className="text-center text-slate-500 py-12">Aucun marchand trouvé</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-merchants-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Marchand</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Plan</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Inscription</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Cartes</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Clients</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Scans</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Trial</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{m.business_name || m.name}</p>
                        <p className="text-xs text-slate-500">{m.email}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${PLAN_BADGES[m.subscription_plan] || 'bg-slate-100 text-slate-600'}`}>
                          {m.subscription_plan?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{m.created_at?.slice(0, 10)}</td>
                      <td className="py-3 px-4 text-right font-medium">{m.cards_count}</td>
                      <td className="py-3 px-4 text-right font-medium">{m.customers_count}</td>
                      <td className="py-3 px-4 text-right font-medium">{m.scans_count}</td>
                      <td className="py-3 px-4">
                        {m.trial_days_left !== null && m.trial_days_left !== undefined ? (
                          <span className={`text-xs font-medium ${m.trial_days_left <= 3 ? 'text-red-600' : 'text-yellow-600'}`}>
                            {m.trial_days_left}j restants
                          </span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="py-3 px-4">
                        <Link to={`/admin/merchants/${m.id}`}>
                          <Button variant="ghost" size="sm" data-testid={`view-merchant-${m.id}`}>Voir</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{total} marchands au total</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMerchantsPage;
